import * as React from "react";
import {useContext, useEffect, useMemo, useState} from "react";
import {useLazyQuery, useMutation} from "@apollo/client";
import {
    archiveAreaMutation,
    createAreaMutation,
    createInvitationMutation,
    getAreaStatisticsQuery,
    getAreasQuery,
    getOrganizationsQuery,
    purgeAreaMutation
} from "../../components/area/areaQueries";
import {useSearchParams} from "react-router-dom";
import {createDraftMutation, versionsQuery} from "../versions/versionQueries";
import {Field, Header, RowData, TableInternal} from "../../components/table/TableInternal";
import AuthButton from "../../auth/AuthButton";
import {
    Button,
    Box,
    Center,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    Flex,
    FormLabel,
    Grid,
    IconButton,
    Input,
    HStack,
    Image,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Spinner,
    Stack,
    Text,
    Tooltip,
    useToast
} from "@chakra-ui/react";
import AreaAudioStorageSummary from "../../components/area/AreaAudioStorageSummary";
import InstallAppDialog from "../../components/appDownload/InstallAppDialog";
import {QrCodeLink} from "../../components/qr/QrCodeLink";
import {UserContext, UserContextI} from "../../UserContext";
import {BiArchive, BiCheck, BiCog, BiCopy, BiDownload, BiLink, BiLock, BiLockOpen, BiPlus, BiQuestionMark, BiTrashAlt, BiUndo, BiX} from "react-icons/bi";
import {isAdministrativeRole, isRootAdminRole, User} from "../../auth/types";
import {useConfirmDialog} from "../../components/common/ConfirmDialogProvider";
import {AuthContext} from "../../auth/FirebaseAuth";
import {firebaseFunctionsUri} from "../../graphql/apolloClient";
import {AREA_STATUS_ARCHIVED, AREA_STATUS_LIVE, AREA_STATUS_READONLY, AreaStatistics, Version} from "../../components/types";
import HelpLink from "../../components/help/HelpLink";

type OrganizationOption = {
    id: string;
    name: string;
};

const AreaCoverThumb = ({url, alt}: {url?: string; alt: string}) => {
    const authContext = useContext(AuthContext);
    const [authToken, setAuthToken] = useState<string | undefined>(authContext.getToken());

    useEffect(() => {
        const existing = authContext.getToken();
        if (existing) {
            setAuthToken(existing);
            return;
        }
        void authContext.getTokenAsync().then(setAuthToken);
    }, [authContext]);

    const previewUrl = useMemo(() => {
        const trimmed = (url || "").trim();
        if (!trimmed) {
            return undefined;
        }
        if (/^https?:\/\//i.test(trimmed)) {
            return trimmed;
        }
        const separator = trimmed.includes("?") ? "&" : "?";
        const authQuery = authToken ? `${separator}authorization=${encodeURIComponent(`Bearer ${authToken}`)}` : "";
        return `${firebaseFunctionsUri}${trimmed}${authQuery}`;
    }, [url, authToken]);

    return (
        <Box
            width={"40px"}
            height={"40px"}
            borderRadius={"6px"}
            bg={"gray.100"}
            overflow={"hidden"}
            flexShrink={0}
            border={"1px solid"}
            borderColor={"gray.200"}
        >
            {previewUrl ? (
                <Image src={previewUrl} alt={alt} width={"100%"} height={"100%"} objectFit={"cover"}/>
            ) : null}
        </Box>
    );
};

type InviteDialogArea = {
    id: string;
    name: string;
};

type DownloadDialogArea = {
    id: string;
    name: string;
};

type DownloadMode = "full" | "differential" | "configurationOnly";
const AREA_LIST_ORGANIZATION_FILTER_STORAGE_KEY = "areaList.organizationFilter";

const isValidInviteEmail = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
        return true;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

const formatDownloadVersionLabel = (version: Version) => {
    const publishedAtLabel = new Date(getVersionPublishedAt(version) || 0).toLocaleString("da-DK");
    const releaseNote = (version.releaseNote || "").trim();
    return releaseNote ? `${publishedAtLabel} - ${releaseNote}` : publishedAtLabel;
};

const getVersionPublishedAt = (version?: Partial<Version>) => {
    return version?.publishedAt || 0;
};

const AreaList = () => {
    const userContext = useContext<UserContextI>(UserContext)
    const authContext = useContext(AuthContext);
    const {confirm} = useConfirmDialog();
    const toast = useToast();
    const [search, setSearch] = useSearchParams();

    const downloadBaseUrl = useMemo(() => {
        if (window.location.hostname === "localhost") {
            return firebaseFunctionsUri;
        }
        const projectId = "fire-starter-77890";
        const region = "europe-west2";
        return `https://${region}-${projectId}.cloudfunctions.net`;
    }, []);

    const [getAreas, {loading: loadingAreaList, data: areaListData}] = useLazyQuery(getAreasQuery);
    const [getOrganizations, {data: organizationsData}] = useLazyQuery(getOrganizationsQuery);
    const [createArea, {loading: creatingArea}] = useMutation(createAreaMutation);
    const [createDraft, {loading: creatingDraft}] = useMutation(createDraftMutation);
    const [archiveArea, {loading: archivingArea}] = useMutation(archiveAreaMutation);
    const [purgeArea, {loading: purgingArea}] = useMutation(purgeAreaMutation);
    const [createInvitation, {loading: creatingInvitation}] = useMutation(createInvitationMutation);

    const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
    const [isInstallDialogOpen, setInstallDialogOpen] = useState<boolean>(false);
    const [downloadingAreaId, setDownloadingAreaId] = useState<string | null>(null);
    const [newAreaName, setNewAreaName] = useState<string>("");
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>("");
    const [creatingInvitationAreaId, setCreatingInvitationAreaId] = useState<string | null>(null);
    const [inviteDialogArea, setInviteDialogArea] = useState<InviteDialogArea | null>(null);
    const [inviteEmail, setInviteEmail] = useState<string>("");
    const [downloadDialogArea, setDownloadDialogArea] = useState<DownloadDialogArea | null>(null);
    const [downloadMode, setDownloadMode] = useState<DownloadMode>("full");
    const [selectedDownloadVersionId, setSelectedDownloadVersionId] = useState<string>("");
    const [organizationFilterId, setOrganizationFilterId] = useState<string>("");

    const [getDownloadVersions, {data: downloadVersionsData, loading: loadingDownloadVersions, error: downloadVersionsError}] = useLazyQuery(versionsQuery, {
        fetchPolicy: "no-cache"
    });

    const currentUser = userContext.getUser();
    const isRootAdmin = isRootAdminRole(currentUser?.role);
    const isAdministrativeUser = isAdministrativeRole(currentUser?.role);
    const [loadAreaStatistics, {data: areaStatisticsData}] = useLazyQuery(getAreaStatisticsQuery, {
        fetchPolicy: "network-only"
    });
    const areaStatisticsByAreaId = useMemo(() => {
        const map = new Map<string, AreaStatistics>();
        const entries: AreaStatistics[] = areaStatisticsData?.getAreaStatistics || [];
        entries.forEach((entry) => {
            if (entry?.areaId) {
                map.set(entry.areaId, entry);
            }
        });
        return map;
    }, [areaStatisticsData]);

    useEffect(() => {
        const user: User | undefined = currentUser;
        if (user) {
            const organizationIds = user.organizations || [];
            getAreas({
                variables: {
                    organizationIds: organizationIds,
                    includeArchived: isAdministrativeUser
                }
            })
            getOrganizations({
                variables: {
                    organizationIds: isRootAdmin ? [] : organizationIds,
                }
            })
        }
    }, [currentUser, getAreas, getOrganizations, isAdministrativeUser, isRootAdmin]);

    const visibleAreas = useMemo(() => {
        const areas = isAdministrativeUser
            ? (areaListData?.getAreas || [])
            : (areaListData?.getAreas || []).filter((area: any) => (area.status || AREA_STATUS_LIVE) !== AREA_STATUS_ARCHIVED);
        const filteredAreas = organizationFilterId
            ? areas.filter((area: any) => area?.organization?.id === organizationFilterId)
            : areas;
        return [...filteredAreas].sort((left: any, right: any) =>
            String(left?.releasedName || "").localeCompare(String(right?.releasedName || ""), undefined, {sensitivity: "base"})
        );
    }, [areaListData, isAdministrativeUser, organizationFilterId]);

    useEffect(() => {
        const visibleAreaIds = visibleAreas
            .map((area: any) => area?.id)
            .filter((areaId: unknown): areaId is string => typeof areaId === "string" && areaId.length > 0);
        if (visibleAreaIds.length === 0) {
            console.info("[area-stats:list] skipped", {
                visibleAreaIds: visibleAreaIds
            });
            return;
        }
        console.info("[area-stats:list] request", {
            visibleAreaIds: visibleAreaIds
        });
        loadAreaStatistics({
            variables: {
                areaIds: visibleAreaIds
            }
        });
    }, [loadAreaStatistics, visibleAreas]);

    useEffect(() => {
        const entries: AreaStatistics[] = areaStatisticsData?.getAreaStatistics || [];
        console.info("[area-stats:list] response", {
            count: entries.length,
            areaIds: entries.map((entry) => entry.areaId)
        });
    }, [areaStatisticsData]);

    const setAreaID = ((id: string) => {
        search.delete("view");
        search.delete("wizard");
        search.set("areaID", id);
        setSearch(search);
    });

    const setAreaIDWithWizard = (id: string) => {
        search.delete("view");
        search.set("areaID", id);
        search.set("wizard", "1");
        setSearch(search);
    };

    const openOrganizationManagement = () => {
        search.delete("areaID");
        search.set("view", "organizations");
        setSearch(search);
    };

    const openPlanManagement = () => {
        search.delete("areaID");
        search.set("view", "plans");
        setSearch(search);
    };

    const openUserManagement = () => {
        search.delete("areaID");
        search.set("view", "users");
        setSearch(search);
    };

    const organizationOptions = useMemo<OrganizationOption[]>(() => {
        const organizations = organizationsData?.getOrganizations || [];
        if (isRootAdmin) {
            return organizations.map((org: any) => ({
                id: org.id,
                name: org.name || org.id
            }));
        }

        const orgIds = currentUser?.organizations || [];
        const orgNameMap = new Map<string, string>();
        organizations.forEach((org: any) => {
            if (org?.id) {
                orgNameMap.set(org.id, org.name || org.id);
            }
        });
        return orgIds.map((orgId) => {
            const areaMatch = areaListData?.getAreas?.find((area: any) => area.organization?.id === orgId);
            return {
                id: orgId,
                name: orgNameMap.get(orgId) || areaMatch?.organization?.name || orgId
            };
        });
    }, [areaListData, currentUser, isRootAdmin, organizationsData]);

    const defaultAreaName = useMemo(() => {
        const nextIndex = areaListData?.getAreas?.length ? areaListData.getAreas.length + 1 : 1;
        return nextIndex > 1 ? `New space ${nextIndex}` : "New space";
    }, [areaListData]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const storedFilterId = window.localStorage.getItem(AREA_LIST_ORGANIZATION_FILTER_STORAGE_KEY) || "";
        setOrganizationFilterId(storedFilterId);
    }, []);

    useEffect(() => {
        if (!organizationsData?.getOrganizations) {
            return;
        }
        const validOrganizationIds = new Set(organizationOptions.map((organization) => organization.id));
        if (organizationFilterId && !validOrganizationIds.has(organizationFilterId)) {
            setOrganizationFilterId("");
            if (typeof window !== "undefined") {
                window.localStorage.removeItem(AREA_LIST_ORGANIZATION_FILTER_STORAGE_KEY);
            }
        }
    }, [organizationFilterId, organizationOptions, organizationsData]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        if (organizationFilterId) {
            window.localStorage.setItem(AREA_LIST_ORGANIZATION_FILTER_STORAGE_KEY, organizationFilterId);
            return;
        }
        window.localStorage.removeItem(AREA_LIST_ORGANIZATION_FILTER_STORAGE_KEY);
    }, [organizationFilterId]);

    const publishedDownloadVersions = useMemo<Version[]>(() => {
        const versions = downloadVersionsData?.getVersions || [];
        return [...versions]
            .filter((version: Version) => getVersionPublishedAt(version) > 0)
            .sort((left: Version, right: Version) => getVersionPublishedAt(right) - getVersionPublishedAt(left));
    }, [downloadVersionsData]);

    useEffect(() => {
        if (!downloadDialogArea) {
            return;
        }
        if (publishedDownloadVersions.length === 0) {
            setSelectedDownloadVersionId("");
            return;
        }

        const hasCurrentSelection = publishedDownloadVersions.some((version: Version) => version.id === selectedDownloadVersionId);
        if (hasCurrentSelection) {
            return;
        }

        const defaultVersion = publishedDownloadVersions[1] || publishedDownloadVersions[0];
        setSelectedDownloadVersionId(defaultVersion.id);
    }, [downloadDialogArea, publishedDownloadVersions, selectedDownloadVersionId]);

    const handleCopyPublicLink = async (url: string, areaName: string) => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                const input = document.createElement("input");
                input.value = url;
                document.body.appendChild(input);
                input.select();
                document.execCommand("copy");
                document.body.removeChild(input);
            }
            toast({
                title: "Link copied",
                description: `Copied public link for ${areaName}.`,
                status: "success",
                isClosable: true
            });
        } catch (error) {
            toast({
                title: "Couldn't copy link",
                description: error instanceof Error ? error.message : "Clipboard access was denied.",
                status: "error",
                isClosable: true
            });
        }
    };

    const openCreateDialog = () => {
        setNewAreaName(defaultAreaName);
        setSelectedOrganizationId(organizationFilterId || organizationOptions[0]?.id || "");
        setDialogOpen(true);
    };

    const handleCreateArea = async () => {
        if (!isAdministrativeUser) {
            return;
        }
        if (!selectedOrganizationId) {
            return;
        }

        const name = newAreaName.trim() || defaultAreaName;

        try {
            const result = await createArea({
                variables: {
                    organizationId: selectedOrganizationId,
                    name: name
                }
            });
            const newArea = result.data?.createArea;
            if (newArea?.id) {
                try {
                    await createDraft({
                        variables: {
                            areaId: newArea.id
                        }
                    });
                } catch (error) {
                    console.error(error);
                }
                setDialogOpen(false);
                setAreaIDWithWizard(newArea.id);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const reloadAreas = () => {
        const user = currentUser;
        if (user) {
            getAreas({
                variables: {
                    organizationIds: user.organizations,
                    includeArchived: isAdministrativeUser
                }
            });
        }
    };

    const handleArchiveArea = async (areaId: string, areaName: string) => {
        if (!isAdministrativeUser) {
            return;
        }
        const approved = await confirm({
            title: "Archive space",
            body: `Archive "${areaName}"?`,
            confirmText: "Archive",
            isDestructive: true
        });
        if (!approved) {
            return;
        }
        try {
            await archiveArea({
                variables: {
                    id: areaId,
                    status: AREA_STATUS_ARCHIVED
                }
            });
            reloadAreas();
        } catch (error) {
            console.error(error);
        }
    };

    const handleReviveArea = async (areaId: string) => {
        if (!isAdministrativeUser) {
            return;
        }
        try {
            await archiveArea({
                variables: {
                    id: areaId,
                    status: AREA_STATUS_LIVE
                }
            });
            reloadAreas();
        } catch (error) {
            console.error(error);
        }
    };

    const handleReadonlyArea = async (areaId: string, areaName: string, hasUnpublishedChanges: boolean) => {
        if (!isAdministrativeUser) {
            return;
        }
        const warningText = hasUnpublishedChanges
            ? `Set "${areaName}" to readonly? Unpublished changes will be lost.`
            : `Set "${areaName}" to readonly?`;
        const approved = await confirm({
            title: "Set space to readonly",
            body: warningText,
            confirmText: "Set readonly",
            isDestructive: true
        });
        if (!approved) {
            return;
        }
        try {
            await archiveArea({
                variables: {
                    id: areaId,
                    status: AREA_STATUS_READONLY
                }
            });
            reloadAreas();
        } catch (error) {
            console.error(error);
        }
    };

    const handlePurgeArea = async (areaId: string, areaName: string) => {
        if (!isRootAdmin) {
            return;
        }
        const approved = await confirm({
            title: "Purge space",
            body: `Permanently delete "${areaName}" and all related data? This cannot be undone.`,
            confirmText: "Purge",
            isDestructive: true
        });
        if (!approved) {
            return;
        }
        try {
            await purgeArea({
                variables: {
                    id: areaId
                }
            });
            reloadAreas();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDownloadArea = async (
        areaId: string,
        options?: {
            downloadType?: DownloadMode;
            basePublishedAt?: number;
        }
    ) => {
        if (!isRootAdmin) {
            return false;
        }
        const token = await authContext.getTokenAsync();
        if (!token) {
            console.error("Missing auth token for download");
            return false;
        }
        setDownloadingAreaId(areaId);
        try {
            const query = new URLSearchParams({
                areaId: areaId,
                mode: "url"
            });
            if (options?.downloadType === "differential") {
                query.set("downloadType", "differential");
                if (options.basePublishedAt) {
                    query.set("basePublishedAt", String(options.basePublishedAt));
                }
            } else if (options?.downloadType === "configurationOnly") {
                query.set("downloadType", "configurationOnly");
            }
            const downloadUrl = `${downloadBaseUrl}/areaDownload?${query.toString()}`;
            const response = await fetch(downloadUrl, {
                headers: {
                    authorization: token
                }
            });
            if (!response.ok) {
                const errorText = await response.text();
                const message = errorText || "Download failed";
                console.error(message);
                toast({
                    title: "Download failed",
                    description: message,
                    status: "error",
                    isClosable: true
                });
                return false;
            }
            const data = await response.json();
            const downloadLink = data?.url;
            if (data?.error) {
                const message = typeof data.error === "string" ? data.error : "Download failed";
                console.error(message);
                toast({
                    title: "Download failed",
                    description: message,
                    status: "error",
                    isClosable: true
                });
                return false;
            }
            if (!downloadLink) {
                const message = "Download link missing.";
                console.error(message);
                toast({
                    title: "Download failed",
                    description: message,
                    status: "error",
                    isClosable: true
                });
                return false;
            }
            const link = document.createElement("a");
            link.href = downloadLink;
            link.rel = "noopener";
            link.click();
            return true;
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : "Unable to download space package.";
            toast({
                title: "Download failed",
                description: message,
                status: "error",
                isClosable: true
            });
            return false;
        } finally {
            setDownloadingAreaId(null);
        }
    };

    const openDownloadDialog = (areaId: string, areaName: string) => {
        setDownloadDialogArea({
            id: areaId,
            name: areaName
        });
        setDownloadMode("full");
        setSelectedDownloadVersionId("");
        getDownloadVersions({variables: {areaId}});
    };

    const closeDownloadDialog = () => {
        if (downloadingAreaId) {
            return;
        }
        setDownloadDialogArea(null);
        setDownloadMode("full");
        setSelectedDownloadVersionId("");
    };

    const handleDownloadConfirm = async () => {
        if (!downloadDialogArea) {
            return;
        }

        const selectedBaseVersion = publishedDownloadVersions.find((version: Version) => version.id === selectedDownloadVersionId);
        const succeeded = await handleDownloadArea(downloadDialogArea.id, {
            downloadType: downloadMode,
            basePublishedAt: downloadMode === "differential" ? getVersionPublishedAt(selectedBaseVersion) : undefined
        });
        if (succeeded) {
            closeDownloadDialog();
        }
    };

    const openInviteDialog = (areaId: string, areaName: string) => {
        setInviteDialogArea({
            id: areaId,
            name: areaName
        });
        setInviteEmail("");
    };

    const closeInviteDialog = (force?: boolean) => {
        if (creatingInvitation && !force) {
            return;
        }
        setInviteDialogArea(null);
        setInviteEmail("");
    };

    const handleInvite = async (
        areaId: string,
        areaName: string,
        email: string | undefined,
        action: "send" | "copy"
    ) => {
        if (!isAdministrativeUser || !areaId) {
            return;
        }
        const trimmedEmail = (email || "").trim();
        if (action === "send" && !trimmedEmail) {
            toast({
                title: "Email required",
                description: "Enter an email address to send the invitation.",
                status: "error",
                isClosable: true
            });
            return;
        }
        if (!isValidInviteEmail(trimmedEmail)) {
            toast({
                title: "Invalid email",
                description: "Enter a valid email address or leave it empty.",
                status: "error",
                isClosable: true
            });
            return;
        }
        try {
            setCreatingInvitationAreaId(areaId);
            const response = await createInvitation({
                variables: {
                    areaId,
                    email: trimmedEmail || null
                }
            });
            const token = response.data?.createInvitation?.token;
            const expiresAt = response.data?.createInvitation?.expiresAt;
            const emailSent = response.data?.createInvitation?.emailSent === true;
            if (!token) {
                const message = "Invite token missing.";
                console.error(message);
                toast({
                    title: "Unable to create invitation",
                    description: message,
                    status: "error",
                    isClosable: true,
                    duration: 8000
                });
                return;
            }
            const inviteUrl = `${window.location.origin}/invite?token=${encodeURIComponent(token)}`;
            const expiresLabel = expiresAt ? ` Expires ${new Date(expiresAt).toLocaleString()}.` : "";

            if (action === "send" && emailSent) {
                toast({
                    title: "Invitation sent",
                    description: `Emailed invitation for ${areaName} to ${trimmedEmail}.${expiresLabel}`,
                    status: "success",
                    isClosable: true
                });
                closeInviteDialog(true);
                return;
            }

            // Either action === "copy", or send was requested but email failed → fall back to copy.
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(inviteUrl);
            } else {
                const input = document.createElement("input");
                input.value = inviteUrl;
                document.body.appendChild(input);
                input.select();
                document.execCommand("copy");
                document.body.removeChild(input);
            }

            if (action === "send" && !emailSent) {
                toast({
                    title: "Couldn't send email — link copied",
                    description: `Email delivery failed for ${trimmedEmail}. The invite link was copied to your clipboard so you can share it manually.${expiresLabel}`,
                    status: "warning",
                    isClosable: true,
                    duration: 8000
                });
            } else {
                toast({
                    title: "Invitation link copied",
                    description: `Copied one-time invite link for ${areaName}${trimmedEmail ? ` (${trimmedEmail})` : ""}.${expiresLabel}`,
                    status: "success",
                    isClosable: true
                });
            }
            closeInviteDialog(true);
        } catch (error) {
            console.error(error);
            toast({
                title: "Unable to create invitation",
                description: error instanceof Error ? error.message : "Unknown error",
                status: "error",
                isClosable: true,
                duration: 8000
            });
        } finally {
            setCreatingInvitationAreaId(null);
        }
    };

    if (loadingAreaList) {
        return (
            <Center width={"100%"} height={"100vh"}>
                <Spinner size={"lg"} thickness={"3px"} color={"blue.500"}/>
            </Center>
        );
    }

    if (!areaListData) {
        return (
            <Center width={"100%"} height={"100vh"}>
                <Spinner size={"lg"} thickness={"3px"} color={"blue.500"}/>
            </Center>
        );
    }

    const iosCompatibilityTooltip = "Whether all spots and zones in this space can be triggered on iOS. False means at least one spot or zone is only reachable via MAC-based beacons. Unknown until the space has been opened at least once after this check was added.";
    const headers: Header[] = [
        {
            title: "Space name"
        },
        {
            title: "Organization"
        },
        {
            title: "iOS compatible",
            tooltip: iosCompatibilityTooltip
        },
        {
            title: "Audio storage"
        },
        {
            title: "QR-Code"
        },
        {
            title: "Link"
        },
        ...(isAdministrativeUser ? [{title: "Actions"}] : [])
    ];
    const rowData: RowData[] = visibleAreas.map((area: any) => {
        const areaStatus = area.status || AREA_STATUS_LIVE;
        const isArchived = areaStatus === AREA_STATUS_ARCHIVED;
        const isReadOnly = areaStatus === AREA_STATUS_READONLY;
        const hasUnpublishedChanges = area.hasUnpublishedChanges === true;
        const qrUrl = `https://soundspots.app/load`
            + `?c=${encodeURIComponent(area.organization.id)}`
            + `&a=${encodeURIComponent(area.id)}`
            + `&n=${encodeURIComponent(area.releasedName)}`;
        const qrLegacyJson = JSON.stringify({
            a: area.id,
            c: area.organization.id,
            n: area.releasedName
        });
        const iosCompatibleTooltip = area.iosCompatible === true
            ? "iOS compatible."
            : area.iosCompatible === false
                ? "Not iOS compatible — at least one spot or zone is only reachable via a MAC-based beacon."
                : "Unknown. Open the space to compute.";
        const iosCompatibleIcon = area.iosCompatible === true
            ? <Box as={"span"} color={"green.500"} display={"inline-flex"} alignItems={"center"} fontSize={"lg"}><BiCheck/></Box>
            : area.iosCompatible === false
                ? <Box as={"span"} color={"red.500"} display={"inline-flex"} alignItems={"center"} fontSize={"lg"}><BiX/></Box>
                : <Box as={"span"} color={"gray.500"} display={"inline-flex"} alignItems={"center"} fontSize={"md"}><BiQuestionMark/></Box>;
        const fields: Field[] = [
            {
                element: (
                    <HStack spacing={3} align={"center"}>
                        <AreaCoverThumb url={area.coverImageUrl} alt={area.releasedName}/>
                        <Text noOfLines={2}>{area.releasedName}</Text>
                    </HStack>
                )
            },
            {
                data: area.organization.name
            },
            {
                element: (
                    <Tooltip label={iosCompatibleTooltip}>
                        {iosCompatibleIcon}
                    </Tooltip>
                )
            },
            {
                element: <AreaAudioStorageSummary statistics={areaStatisticsByAreaId.get(area.id)} variant={"compact"}/>
            },
            {
                element: <QrCodeLink value={qrUrl} legacyValue={qrLegacyJson} label={area.releasedName} />,
                disableOnClick: true
            },
            {
                element: (
                    <Tooltip label={"Copy link"}>
                        <IconButton
                            aria-label={"Copy link"}
                            icon={<BiCopy/>}
                            size={"sm"}
                            variant={"ghost"}
                            onClick={() => handleCopyPublicLink(qrUrl, area.releasedName)}
                        />
                    </Tooltip>
                ),
                disableOnClick: true
            }
        ];

        if (isAdministrativeUser) {
            const actionElement = isArchived ? (
                <HStack spacing={1} minWidth={"max-content"} wrap={"nowrap"}>
                    <Tooltip label={"Revive space"}>
                        <IconButton
                            aria-label={"Revive space"}
                            icon={<BiUndo/>}
                            size={"sm"}
                            variant={"ghost"}
                            colorScheme={"green"}
                            isDisabled={archivingArea || purgingArea}
                            onClick={() => handleReviveArea(area.id)}
                        />
                    </Tooltip>
                    {isRootAdmin ? (
                        <Tooltip label={"Purge space"}>
                            <IconButton
                                aria-label={"Purge space"}
                                icon={<BiTrashAlt/>}
                                size={"sm"}
                                variant={"ghost"}
                                colorScheme={"red"}
                                isDisabled={purgingArea}
                                onClick={() => handlePurgeArea(area.id, area.releasedName)}
                            />
                        </Tooltip>
                    ) : null}
                    <Tooltip label={"Copy invitation link"}>
                        <IconButton
                            aria-label={"Copy invitation link"}
                            icon={<BiLink/>}
                            size={"sm"}
                            variant={"ghost"}
                            colorScheme={"teal"}
                            isLoading={creatingInvitationAreaId === area.id}
                            isDisabled={creatingInvitation || creatingInvitationAreaId === area.id}
                            onClick={() => openInviteDialog(area.id, area.releasedName)}
                        />
                    </Tooltip>
                    {isRootAdmin ? (
                        <Tooltip label={downloadingAreaId === area.id ? "Preparing download..." : "Download space package"}>
                            <IconButton
                                aria-label={"Download space package"}
                                icon={<BiDownload/>}
                                size={"sm"}
                                variant={"ghost"}
                                colorScheme={"blue"}
                                isLoading={downloadingAreaId === area.id}
                                isDisabled={downloadingAreaId === area.id}
                                onClick={() => openDownloadDialog(area.id, area.releasedName)}
                            />
                        </Tooltip>
                    ) : null}
                </HStack>
            ) : (
                <HStack spacing={1} minWidth={"max-content"} wrap={"nowrap"}>
                    {isReadOnly ? (
                        <Tooltip label={"Enable editing"}>
                            <IconButton
                                aria-label={"Enable editing"}
                                icon={<BiLockOpen/>}
                                size={"sm"}
                                variant={"ghost"}
                                colorScheme={"blue"}
                                isDisabled={archivingArea}
                                onClick={() => handleReviveArea(area.id)}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip label={"Set readonly"}>
                            <IconButton
                                aria-label={"Set readonly"}
                                icon={<BiLock/>}
                                size={"sm"}
                                variant={"ghost"}
                                colorScheme={"orange"}
                                isDisabled={archivingArea}
                                onClick={() => handleReadonlyArea(area.id, area.releasedName, hasUnpublishedChanges)}
                            />
                        </Tooltip>
                    )}
                    <Tooltip label={"Archive space"}>
                        <IconButton
                            aria-label={"Archive space"}
                            icon={<BiArchive/>}
                            size={"sm"}
                            variant={"ghost"}
                            isDisabled={archivingArea}
                            onClick={() => handleArchiveArea(area.id, area.releasedName)}
                        />
                    </Tooltip>
                    <Tooltip label={"Copy invitation link"}>
                        <IconButton
                            aria-label={"Copy invitation link"}
                            icon={<BiLink/>}
                            size={"sm"}
                            variant={"ghost"}
                            colorScheme={"teal"}
                            isLoading={creatingInvitationAreaId === area.id}
                            isDisabled={creatingInvitation || creatingInvitationAreaId === area.id}
                            onClick={() => openInviteDialog(area.id, area.releasedName)}
                        />
                    </Tooltip>
                    {isRootAdmin ? (
                        <Tooltip label={downloadingAreaId === area.id ? "Preparing download..." : "Download space package"}>
                            <IconButton
                                aria-label={"Download space package"}
                                icon={<BiDownload/>}
                                size={"sm"}
                                variant={"ghost"}
                                colorScheme={"blue"}
                                isLoading={downloadingAreaId === area.id}
                                isDisabled={downloadingAreaId === area.id}
                                onClick={() => openDownloadDialog(area.id, area.releasedName)}
                            />
                        </Tooltip>
                    ) : null}
                </HStack>
            );

            fields.push({
                element: (
                    <Box display={"inline-flex"} minWidth={"max-content"}>
                        {actionElement}
                    </Box>
                ),
                noWrap: true,
                disableOnClick: true
            });
        }

        return {
            id: area.id,
            fields: fields,
            rowClassName: isArchived ? "tableRow-archived" : undefined
        } as RowData
    })

    const canCreateArea = isAdministrativeUser && organizationOptions.length > 0;
    const showOrganizationSelect = organizationOptions.length > 1;
    const showOrganizationFilter = organizationOptions.length > 1;

    return <Grid width={"inherit"} height={"100%"} id={"Grid"} gridTemplateRows={"40px minmax(0, 1fr) 40px"}>
        <Grid gridTemplateColumns={"minmax(0, 320px) 1fr max-content max-content max-content max-content max-content"} alignItems={"center"} columnGap={2}>
            {showOrganizationFilter ? (
                <FormControl maxW={"320px"}>
                    <Select
                        size={"sm"}
                        value={organizationFilterId}
                        onChange={(event) => setOrganizationFilterId(event.target.value)}
                    >
                        <option value={""}>All organizations</option>
                        {organizationOptions.map((org: OrganizationOption) => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                    </Select>
                </FormControl>
            ) : organizationOptions.length === 1 ? (
                <Text fontSize={"sm"} fontWeight={"semibold"} paddingX={2} noOfLines={1}>
                    {organizationOptions[0].name}
                </Text>
            ) : (
                <div></div>
            )}
            <div></div>
            {isAdministrativeUser ? (
                <Button
                    leftIcon={<BiPlus/>}
                    size={"sm"}
                    variant={"outline"}
                    onClick={openCreateDialog}
                    isDisabled={!canCreateArea}
                >
                    Add new space
                </Button>
            ) : (
                <div></div>
            )}
            <Button
                leftIcon={<BiDownload/>}
                size={"sm"}
                variant={"outline"}
                onClick={() => setInstallDialogOpen(true)}
            >
                Install app
            </Button>
            {isAdministrativeUser ? (
                <Menu>
                    <MenuButton
                        as={IconButton}
                        aria-label={"Space list actions"}
                        icon={<BiCog/>}
                        size={"sm"}
                        variant={"outline"}
                    />
                    <MenuList zIndex={20}>
                        <MenuItem onClick={openUserManagement}>
                            Manage users
                        </MenuItem>
                        {isRootAdmin ? (
                            <MenuItem onClick={openOrganizationManagement}>
                                Manage organizations
                            </MenuItem>
                        ) : null}
                        {isRootAdmin ? (
                            <MenuItem onClick={openPlanManagement}>
                                Manage organization plans
                            </MenuItem>
                        ) : null}
                    </MenuList>
                </Menu>
            ) : (
                <div></div>
            )}
            <AuthButton/>
            <HelpLink path={"/spaces/"} label={"Open documentation: Spaces"}/>
        </Grid>
        <Grid minH={0}>
            {visibleAreas.length === 0 ? (
                <Flex
                    direction={"column"}
                    align={"center"}
                    justify={"center"}
                    height={"100%"}
                    minHeight={"200px"}
                    color={"gray.500"}
                    px={4}
                    textAlign={"center"}
                >
                    <Text fontSize={"md"} mb={1} color={"gray.700"} fontWeight={"semibold"}>
                        No spaces yet
                    </Text>
                    <Text fontSize={"sm"} mb={4}>
                        {organizationFilterId
                            ? "This organization doesn't have any spaces yet."
                            : "Create your first space to get started."}
                    </Text>
                    {canCreateArea ? (
                        <Button
                            leftIcon={<BiPlus/>}
                            size={"sm"}
                            variant={"outline"}
                            onClick={openCreateDialog}
                        >
                            Add first space
                        </Button>
                    ) : null}
                </Flex>
            ) : (
                <TableInternal
                    headers={headers}
                    rows={rowData}
                    onClick={setAreaID}
                    stickyHeader={true}
                    containerProps={{
                        height: "100%",
                        overflowY: "auto",
                        overflowX: "auto"
                    }}
                />
            )}
        </Grid>
        <Modal isOpen={isDialogOpen} onClose={() => setDialogOpen(false)} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Create new space</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl>
                            <FormLabel>Space name</FormLabel>
                            <Input
                                value={newAreaName}
                                onChange={(event) => setNewAreaName(event.target.value)}
                                placeholder={"New space"}
                            />
                        </FormControl>
                        {showOrganizationSelect ? (
                            <FormControl>
                                <FormLabel>Organization</FormLabel>
                                <Select
                                    value={selectedOrganizationId}
                                    onChange={(event) => setSelectedOrganizationId(event.target.value)}
                                >
                                    {organizationOptions.map((org: OrganizationOption) => (
                                        <option key={org.id} value={org.id}>{org.name}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : null}
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant='ghost' mr={3} onClick={() => setDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme='blue'
                        onClick={handleCreateArea}
                        isLoading={creatingArea || creatingDraft}
                        isDisabled={!selectedOrganizationId || !canCreateArea}
                    >
                        Create space
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
        <Modal isOpen={!!downloadDialogArea} onClose={closeDownloadDialog} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Download space package</ModalHeader>
                <ModalCloseButton isDisabled={!!downloadingAreaId}/>
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl>
                            <FormLabel>Space</FormLabel>
                            <Input value={downloadDialogArea?.name || ""} isReadOnly/>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Download type</FormLabel>
                            <Select
                                value={downloadMode}
                                onChange={(event) => setDownloadMode(event.target.value as DownloadMode)}
                                isDisabled={!!downloadingAreaId}
                            >
                                <option value={"full"}>Full download</option>
                                <option value={"differential"}>Differential download</option>
                                <option value={"configurationOnly"}>Configuration only</option>
                            </Select>
                        </FormControl>
                        {downloadMode === "differential" ? (
                            <FormControl isDisabled={loadingDownloadVersions || !!downloadingAreaId}>
                                <FormLabel>Compare against version</FormLabel>
                                {loadingDownloadVersions ? (
                                    <Center py={4}>
                                        <Spinner size={"sm"} thickness={"2px"} color={"blue.500"}/>
                                    </Center>
                                ) : (
                                    <Select
                                        value={selectedDownloadVersionId}
                                        onChange={(event) => setSelectedDownloadVersionId(event.target.value)}
                                        placeholder={publishedDownloadVersions.length > 0 ? "Select version" : "No published versions"}
                                    >
                                        {publishedDownloadVersions.map((version: Version) => (
                                            <option key={version.id} value={version.id}>
                                                {formatDownloadVersionLabel(version)}
                                            </option>
                                        ))}
                                    </Select>
                                )}
                                <FormHelperText>
                                    Downloads the latest configuration and only the sound files that are new compared to the selected version.
                                </FormHelperText>
                                <FormHelperText>
                                    Comparison is based on file presence and path in the package. Files with the same stored path and name are treated as existing, even if their content changed.
                                </FormHelperText>
                                {downloadVersionsError ? (
                                    <FormErrorMessage display={"block"}>
                                        Unable to load versions: {downloadVersionsError.message}
                                    </FormErrorMessage>
                                ) : null}
                            </FormControl>
                        ) : null}
                        {downloadMode === "configurationOnly" ? (
                            <FormControl isDisabled={true}>
                                <FormHelperText mt={0}>
                                    Downloads only the `SoundSpots.json` configuration as a plain JSON file, without sound files and without a zip archive.
                                </FormHelperText>
                            </FormControl>
                        ) : null}
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant='ghost' mr={3} onClick={closeDownloadDialog} isDisabled={!!downloadingAreaId}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme='blue'
                        onClick={handleDownloadConfirm}
                        isLoading={downloadingAreaId === downloadDialogArea?.id}
                        isDisabled={downloadMode === "differential" && !selectedDownloadVersionId}
                    >
                        Download
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
        <Modal isOpen={!!inviteDialogArea} onClose={closeInviteDialog} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Invite collaborator</ModalHeader>
                <ModalCloseButton isDisabled={creatingInvitation}/>
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl>
                            <FormLabel>Space</FormLabel>
                            <Input value={inviteDialogArea?.name || ""} isReadOnly/>
                        </FormControl>
                        <FormControl isInvalid={!isValidInviteEmail(inviteEmail)}>
                            <FormLabel>Email (optional)</FormLabel>
                            <Input
                                value={inviteEmail}
                                onChange={(event) => setInviteEmail(event.target.value)}
                                placeholder={"name@example.com"}
                                autoFocus
                            />
                            <FormHelperText>
                                If set, only that email can use the invitation link, and the link can be emailed via "Send invitation".
                            </FormHelperText>
                            {!isValidInviteEmail(inviteEmail) ? (
                                <FormErrorMessage>Enter a valid email address or leave it empty.</FormErrorMessage>
                            ) : null}
                        </FormControl>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant='ghost' mr={3} onClick={() => closeInviteDialog()} isDisabled={creatingInvitation}>
                        Cancel
                    </Button>
                    <Button
                        variant={"outline"}
                        mr={3}
                        isLoading={creatingInvitation && !!inviteDialogArea}
                        isDisabled={!inviteDialogArea || !isValidInviteEmail(inviteEmail)}
                        onClick={() => {
                            if (!inviteDialogArea) {
                                return;
                            }
                            handleInvite(inviteDialogArea.id, inviteDialogArea.name, inviteEmail, "copy");
                        }}
                    >
                        Copy link
                    </Button>
                    <Button
                        colorScheme='teal'
                        isLoading={creatingInvitation && !!inviteDialogArea}
                        isDisabled={!inviteDialogArea || !isValidInviteEmail(inviteEmail) || !inviteEmail.trim()}
                        onClick={() => {
                            if (!inviteDialogArea) {
                                return;
                            }
                            handleInvite(inviteDialogArea.id, inviteDialogArea.name, inviteEmail, "send");
                        }}
                    >
                        Send invitation
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
        <InstallAppDialog isOpen={isInstallDialogOpen} onClose={() => setInstallDialogOpen(false)}/>
    </Grid>
}

export default AreaList;
